import React, { Suspense, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import AnalyticsTracker from './components/AnalyticsTracker';
import './App.css';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import AuthenticatedRoute from './components/AuthenticatedRoute';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import WizardLayout from './components/WizardLayout';
import AdminLayout from './components/AdminLayout';
import AdminRoute from './components/AdminRoute';
import EmployerAuthRoute from './components/EmployerAuthRoute';
import WorkerRoute from './components/WorkerRoute';
import { AdminProvider } from './stores/adminStore';
import { useUserStore } from './stores/userStore';
import { PageTransition } from './components/PageTransition';
import { SplashScreen } from './components/SplashScreen';

import LoadingSpinner from './components/LoadingSpinner';

// Eagerly loaded for fast FCP
import WelcomeScreen from './screens/WelcomeScreen';
import MergedSignupScreen from './screens/onboarding/MergedSignupScreen';
import SignInScreen from './screens/LoginScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';

// Lazy loaded feature screens
const OptionalProfileScreen = React.lazy(() => import('./screens/onboarding/OptionalProfileScreen'));
const AboutYouScreen = React.lazy(() => import('./screens/onboarding/AboutYouScreen'));
const SuccessScreen = React.lazy(() => import('./screens/onboarding/SuccessScreen'));
const AccountCreatedPage = React.lazy(() => import('./screens/AccountCreatedPage'));
const DashboardScreen = React.lazy(() => import('./screens/DashboardScreen'));
const DiscoverScreen = React.lazy(() => import('./screens/DiscoverScreen'));
const DiscoverCommunityScreen = React.lazy(() => import('./screens/DiscoverCommunityScreen'));
const DiscoverWorkScreen = React.lazy(() => import('./screens/DiscoverWorkScreen'));
const DiscoverLifeScreen = React.lazy(() => import('./screens/DiscoverLifeScreen'));
const NotificationsScreen = React.lazy(() => import('./screens/NotificationsScreen'));
const EditProfileScreen = React.lazy(() => import('./screens/EditProfileScreen'));
const PublicProfileScreen = React.lazy(() => import('./screens/PublicProfileScreen'));
const ProfileSettingsScreen = React.lazy(() => import('./screens/ProfileSettingsScreen'));
const GroupsListScreen = React.lazy(() => import('./screens/GroupsListScreen'));
const GroupFeedScreen = React.lazy(() => import('./screens/GroupFeedScreen'));
const NeedHelpScreen = React.lazy(() => import('./screens/NeedHelpScreen'));
const HelpRequestDetailScreen = React.lazy(() => import('./screens/HelpRequestDetailScreen'));
const CanHelpScreen = React.lazy(() => import('./screens/CanHelpScreen'));
const CommunityHelpScreen = React.lazy(() => import('./screens/CommunityHelpScreen'));
const RidesCarpoolScreen = React.lazy(() => import('./screens/RidesCarpoolScreen'));
const ServiceOfferDetailScreen = React.lazy(() => import('./screens/ServiceOfferDetailScreen'));
const HangoutScreen = React.lazy(() => import('./screens/HangoutScreen'));
const MarketplaceScreen = React.lazy(() => import('./screens/MarketplaceScreen'));

// Business & Jobs
const AddBusinessScreen = React.lazy(() => import('./screens/AddBusinessScreen'));
const EditBusinessScreen = React.lazy(() => import('./screens/EditBusinessScreen'));
const SearchJobsScreen = React.lazy(() => import('./screens/JobSearchScreen'));
const JobDetailsScreen = React.lazy(() => import('./screens/JobDetailsScreen'));
const ApplyToJobScreen = React.lazy(() => import('./screens/ApplyToJobScreen'));
const PostJobScreen = React.lazy(() => import('./screens/PostJobScreen'));
const JobsAppliedScreen = React.lazy(() => import('./screens/JobsAppliedScreen'));
const JobsPostedScreen = React.lazy(() => import('./screens/JobsPostedScreen'));
const JobApplicationsScreen = React.lazy(() => import('./screens/JobApplicationsScreen'));
const EmployerDashboardScreen = React.lazy(() => import('./screens/EmployerDashboardScreen'));
const EmployerOnboardingScreen = React.lazy(() => import('./screens/EmployerOnboardingScreen'));
const EmployerNetworkScreen = React.lazy(() => import('./screens/EmployerNetworkScreen'));
const EmployerSignupScreen = React.lazy(() => import('./screens/EmployerSignupScreen'));
const EmployerLoginScreen = React.lazy(() => import('./screens/EmployerLoginScreen'));
const EmployerPricingScreen = React.lazy(() => import('./screens/EmployerPricingScreen'));
const ProPricingScreen = React.lazy(() => import('./screens/ProPricingScreen'));
const EmployerBrowseWorkersScreen = React.lazy(() => import('./screens/EmployerBrowseWorkersScreen'));
const EmployerProfileScreen = React.lazy(() => import('./screens/EmployerProfileScreen'));
const EmployerCompanyProfileScreen = React.lazy(() => import('./screens/EmployerCompanyProfileScreen'));
const EmployerMyReviewsScreen = React.lazy(() => import('./screens/EmployerMyReviewsScreen'));
const WorkerNetworkScreen = React.lazy(() => import('./screens/WorkerNetworkScreen'));

// Events
const EventsScreen = React.lazy(() => import('./screens/EventsScreen'));

// Social & Meetups
const MeetupsScreen = React.lazy(() => import('./screens/MeetupsScreen'));
const MeetupDetailsScreen = React.lazy(() => import('./screens/MeetupDetailsScreen'));
const PostMeetupScreen = React.lazy(() => import('./screens/PostMeetupScreen'));
const MeetupsPostedScreen = React.lazy(() => import('./screens/MeetupsPostedScreen'));
const MeetupsInterestedScreen = React.lazy(() => import('./screens/MeetupsInterestedScreen'));
const EditMeetupScreen = React.lazy(() => import('./screens/EditMeetupScreen'));
const ConnectScreen = React.lazy(() => import('./screens/ConnectScreen'));
const WhoIsAroundScreen = React.lazy(() => import('./screens/WhoIsAroundScreen'));
const VisaDeadlineScreen = React.lazy(() => import('./screens/VisaDeadlineScreen'));
const LayoffGuideScreen = React.lazy(() => import('./screens/LayoffGuideScreen'));
const ScamAlertsScreen = React.lazy(() => import('./screens/ScamAlertsScreen'));
const TransferRequestsScreen = React.lazy(() => import('./screens/TransferRequestsScreen'));
const LegalResourcesScreen = React.lazy(() => import('./screens/LegalResourcesScreen'));
const ChatScreen = React.lazy(() => import('./screens/ChatScreen'));

// What to Know
const WhatToKnowScreen = React.lazy(() => import('./screens/WhatToKnowScreen'));
const SavedAnswersScreen = React.lazy(() => import('./screens/SavedAnswersScreen'));
const DocumentVaultScreen = React.lazy(() => import('./screens/DocumentVaultScreen'));

// Tips & Advice
const TipsTripsAdviceScreen = React.lazy(() => import('./screens/TipsTripsAdviceScreen'));
const TipsTripsAdviceDetailScreen = React.lazy(() => import('./screens/TipsTripsAdviceDetailScreen'));
const UserTipsTripsAdviceScreen = React.lazy(() => import('./screens/UserTipsTripsAdviceScreen'));
const PostTipsTripsAdviceScreen = React.lazy(() => import('./screens/PostTipsTripsAdviceScreen'));

// Wizard Screens
const BackgroundScreen = React.lazy(() => import('./screens/wizard/BackgroundScreen'));
const LifestyleScreen = React.lazy(() => import('./screens/wizard/LifestyleScreen'));
const TravelExplorationScreen = React.lazy(() => import('./screens/wizard/TravelExplorationScreen'));
const KnowledgeCommunityScreen = React.lazy(() => import('./screens/wizard/KnowledgeCommunityScreen'));

// Admin Screens
const AdminLoginScreen = React.lazy(() => import('./screens/admin/AdminLoginScreen'));
const AdminDashboardScreen = React.lazy(() => import('./screens/admin/AdminDashboardScreen'));
const BusinessListScreen = React.lazy(() => import('./screens/admin/BusinessListScreen'));
const BusinessDetailScreen = React.lazy(() => import('./screens/admin/BusinessDetailScreen'));
const TipsTripsAdviceListScreen = React.lazy(() => import('./screens/admin/TipsTripsAdviceListScreen'));
const AdminPostTipsTripsAdviceScreen = React.lazy(() => import('./screens/admin/PostTipsTripsAdviceScreen'));
const AdminEditTipsTripsAdviceScreen = React.lazy(() => import('./screens/admin/EditTipsTripsAdviceScreen'));
const ViewTipsTripsAdviceScreen = React.lazy(() => import('./screens/admin/ViewTipsTripsAdviceScreen'));
const UsersListScreen = React.lazy(() => import('./screens/admin/UsersListScreen'));
const EmployersListScreen = React.lazy(() => import('./screens/admin/EmployersListScreen'));
const ReportsListScreen = React.lazy(() => import('./screens/admin/ReportsListScreen'));
const AdminReportEditScreen = React.lazy(() => import('./screens/admin/AdminReportEditScreen'));
const AdminUserViewScreen = React.lazy(() => import('./screens/admin/AdminUserViewScreen'));
const AdminUserEditScreen = React.lazy(() => import('./screens/admin/AdminUserEditScreen'));
const AdminEmployerViewScreen = React.lazy(() => import('./screens/admin/AdminEmployerViewScreen'));
const AdminEmployerEditScreen = React.lazy(() => import('./screens/admin/AdminEmployerEditScreen'));
const AdminMeetupsScreen = React.lazy(() => import('./screens/admin/AdminMeetupsScreen'));
const AdminMarketplaceScreen = React.lazy(() => import('./screens/admin/AdminMarketplaceScreen'));
const AdminHelpRequestsScreen = React.lazy(() => import('./screens/admin/AdminHelpRequestsScreen'));
const AdminServiceOffersScreen = React.lazy(() => import('./screens/admin/AdminServiceOffersScreen'));
const AdminJobsScreen = React.lazy(() => import('./screens/admin/AdminJobsScreen'));
const AdminVerificationsScreen = React.lazy(() => import('./screens/admin/AdminVerificationsScreen'));
const AdminBadgesScreen = React.lazy(() => import('./screens/admin/AdminBadgesScreen'));

// Professional Profile & Employer Registration
const ProfessionalProfileScreen = React.lazy(() => import('./screens/ProfessionalProfileScreen'));

// Company Reviews — open to all authenticated users.
const CompanyReviewsScreen = React.lazy(() => import('./screens/CompanyReviewsScreen'));
const EmployerReviewsScreen = React.lazy(() => import('./screens/EmployerReviewsScreen'));
const WriteReviewScreen = React.lazy(() => import('./screens/WriteReviewScreen'));
const AdminReviewsScreen = React.lazy(() => import('./screens/AdminReviewsScreen'));
const ReviewCampaignScreen = React.lazy(() => import('./screens/ReviewCampaignScreen'));
const ReferralsScreen = React.lazy(() => import('./screens/ReferralsScreen'));
const SocialReferralsScreen = React.lazy(() => import('./screens/SocialReferralsScreen'));
const WhereNextScreen = React.lazy(() => import('./screens/WhereNextScreen'));

// Trust & Verification
const VerificationScreen = React.lazy(() => import('./screens/VerificationScreen'));

// Legal Pages
const PrivacyPolicyScreen = React.lazy(() => import('./screens/PrivacyPolicyScreen'));
const TermsScreen = React.lazy(() => import('./screens/TermsScreen'));
const PortabilityRuleScreen = React.lazy(() => import('./screens/PortabilityRuleScreen'));
const DeleteAccountScreen = React.lazy(() => import('./screens/DeleteAccountScreen'));
const SafetyStandardsScreen = React.lazy(() => import('./screens/SafetyStandardsScreen'));

/** If already logged in, skip the landing page and go straight to dashboard */
const HomeRoute: React.FC = () => {
  const { isAuthenticated, hasToken } = useUserStore();
  const [hydrated, setHydrated] = React.useState(
    useUserStore.persist.hasHydrated()
  );

  React.useEffect(() => {
    if (hydrated) return;
    const unsub = useUserStore.persist.onFinishHydration(() => {
      setHydrated(true);
    });
    return unsub;
  }, [hydrated]);

  // Wait for Zustand to restore persisted state before deciding.
  // Without this, on page reload isAuthenticated is always false (the default)
  // so the user sees WelcomeScreen even though they are logged in.
  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated && hasToken) {
    return <Navigate to="/dashboard" replace />;
  }
  return <WelcomeScreen />;
};

const NotFoundPage: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 text-center px-4">
      <div className="text-7xl mb-6">🧭</div>
      <h1 className="text-3xl font-bold text-slate-900 mb-3">{t('not_found.title')}</h1>
      <p className="text-slate-500 mb-8 max-w-sm">{t('not_found.body')}</p>
      <a
        href="/"
        className="px-6 py-3 bg-sky-600 text-white font-bold rounded-xl hover:bg-sky-700 transition"
      >
        {t('not_found.go_home')}
      </a>
    </div>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [splashVisible, setSplashVisible] = useState(true);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setSplashVisible(false), 1300);
    const removeTimer = setTimeout(() => setShowSplash(false), 1500);
    return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
  }, []);

  // Initialize native features
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      // Set status bar style
      StatusBar.setStyle({ style: Style.Dark });
      StatusBar.setBackgroundColor({ color: '#0f172a' });

      // Handle Android back button
      CapApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          CapApp.exitApp();
        }
      });
    }
  }, []);

  return (
    <>
      {showSplash && (
        <div className={`transition-opacity duration-200 ${splashVisible ? 'opacity-100' : 'opacity-0'}`}>
          <SplashScreen />
        </div>
      )}
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner size="lg" />
        </div>
      }>
        <AnalyticsTracker />
        <PageTransition>
        <Routes>
          {/* Public routes - no authentication required */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="/create-account" element={<MergedSignupScreen />} />
          <Route path="/optional-profile" element={<OptionalProfileScreen />} />
          <Route path="/about-you" element={<AboutYouScreen />} />
          <Route path="/signup-success" element={<SuccessScreen />} />
          <Route path="/welcome" element={<SuccessScreen />} />
          <Route path="/sign-in" element={<SignInScreen />} />
          <Route path="/privacy" element={<PrivacyPolicyScreen />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyScreen />} />
          <Route path="/terms" element={<TermsScreen />} />
          <Route path="/portability-rule" element={<PortabilityRuleScreen />} />
          <Route path="/delete-account" element={<DeleteAccountScreen />} />
          <Route path="/safety-standards" element={<SafetyStandardsScreen />} />
          <Route path="/child-safety" element={<SafetyStandardsScreen />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
          <Route path="/sign-up" element={<Navigate to="/create-account" replace />} />
          <Route path="/review-campaign/:token" element={<ReviewCampaignScreen />} />

          {/* Protected routes - authentication required */}
          <Route
            path="/dashboard"
            element={
              <AuthenticatedRoute>
                <WorkerRoute>
                  <DashboardScreen />
                </WorkerRoute>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/discover"
            element={
              <AuthenticatedRoute>
                <DiscoverScreen />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/discover/community"
            element={
              <AuthenticatedRoute>
                <DiscoverCommunityScreen />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/discover/work"
            element={
              <AuthenticatedRoute>
                <DiscoverWorkScreen />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/discover/life"
            element={
              <AuthenticatedRoute>
                <DiscoverLifeScreen />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/community"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/community/feed"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="/groups"
            element={
              <AuthenticatedRoute>
                <WorkerRoute>
                  <AuthenticatedLayout>
                    <GroupsListScreen />
                  </AuthenticatedLayout>
                </WorkerRoute>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/groups/:stateCode"
            element={
              <AuthenticatedRoute>
                <WorkerRoute>
                  <AuthenticatedLayout>
                    <GroupFeedScreen />
                  </AuthenticatedLayout>
                </WorkerRoute>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/community-help"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <CommunityHelpScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/rides-carpools"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <RidesCarpoolScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/community/need-help"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <NeedHelpScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/community/need-help/:postId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <NeedHelpScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/community/help-request/:id"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <HelpRequestDetailScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/community/can-help"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <CanHelpScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/community/can-help/:postId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <CanHelpScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/community/service-offer/:id"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ServiceOfferDetailScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/community/hangout"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <HangoutScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/community/hangout/:postId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <HangoutScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/marketplace"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <MarketplaceScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/events"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <EventsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/what-to-know"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <WhatToKnowScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/saved-answers"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <SavedAnswersScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <DocumentVaultScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <NotificationsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/tips-trips-advice"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <TipsTripsAdviceScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/post-tips-trips-advice"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <PostTipsTripsAdviceScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/tips-trips-advice/posted"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <UserTipsTripsAdviceScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/edit-profile"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <EditProfileScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/add-business"
            element={
              <EmployerAuthRoute>
                <AuthenticatedLayout>
                  <AddBusinessScreen />
                </AuthenticatedLayout>
              </EmployerAuthRoute>
            }
          />
          <Route
            path="/edit-business/:id"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <EditBusinessScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/public-profile"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <PublicProfileScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/public-profile/:userId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <PublicProfileScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <PublicProfileScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/profile/:userId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <PublicProfileScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/social"
            element={<Navigate to="/community/hangout" replace />}
          />
          <Route
            path="/work"
            element={<Navigate to="/search-jobs" replace />}
          />
          <Route
            path="/search-jobs"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <SearchJobsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/job/:jobId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <JobDetailsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/apply/:jobId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ApplyToJobScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/post-job"
            element={
              <EmployerAuthRoute>
                <AuthenticatedLayout>
                  <PostJobScreen />
                </AuthenticatedLayout>
              </EmployerAuthRoute>
            }
          />
          <Route
            path="/employer-onboarding"
            element={
              <EmployerAuthRoute>
                <AuthenticatedLayout>
                  <EmployerOnboardingScreen />
                </AuthenticatedLayout>
              </EmployerAuthRoute>
            }
          />
          <Route
            path="/meetups"
            element={
              <AuthenticatedRoute>
                <WorkerRoute>
                  <AuthenticatedLayout>
                    <MeetupsScreen />
                  </AuthenticatedLayout>
                </WorkerRoute>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/meetups/:meetupId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <MeetupDetailsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/post-meetup"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <PostMeetupScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/meetups-posted"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <MeetupsPostedScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/edit-meetup/:meetupId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <EditMeetupScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/meetups-interested"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <MeetupsInterestedScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/jobs-applied"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <JobsAppliedScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/jobs-posted"
            element={
              <AuthenticatedRoute>
                <JobsPostedScreen />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/job-applications/:jobId"
            element={
              <AuthenticatedRoute>
                <JobApplicationsScreen />
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/employer-dashboard"
            element={
              <EmployerAuthRoute>
                <AuthenticatedLayout>
                  <EmployerDashboardScreen />
                </AuthenticatedLayout>
              </EmployerAuthRoute>
            }
          />

          <Route
            path="/employer-network"
            element={
              <EmployerAuthRoute>
                <AuthenticatedLayout>
                  <EmployerNetworkScreen />
                </AuthenticatedLayout>
              </EmployerAuthRoute>
            }
          />
          <Route
            path="/employer-browse-workers"
            element={
              <EmployerAuthRoute>
                <AuthenticatedLayout>
                  <EmployerBrowseWorkersScreen />
                </AuthenticatedLayout>
              </EmployerAuthRoute>
            }
          />
          <Route
            path="/employer-profile"
            element={
              <EmployerAuthRoute>
                <AuthenticatedLayout>
                  <EmployerCompanyProfileScreen />
                </AuthenticatedLayout>
              </EmployerAuthRoute>
            }
          />
          <Route
            path="/employer/my-reviews"
            element={
              <EmployerAuthRoute>
                <AuthenticatedLayout>
                  <EmployerMyReviewsScreen />
                </AuthenticatedLayout>
              </EmployerAuthRoute>
            }
          />

          <Route
            path="/worker-network"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <WorkerNetworkScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          <Route
            path="/connect"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ConnectScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/whos-around"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <WhoIsAroundScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/perks"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <SocialReferralsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/visa-deadlines"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <VisaDeadlineScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/layoff-guide"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <LayoffGuideScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/scam-alerts"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ScamAlertsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route path="/transfer-requests" element={<TransferRequestsScreen />} />
          <Route
            path="/legal-resources"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <LegalResourcesScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/need-help"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <NeedHelpScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ChatScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/chat/:conversationId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ChatScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/tips-trips-advice/:postId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <TipsTripsAdviceDetailScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/post-tips-trips-advice/:postId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <PostTipsTripsAdviceScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          {/* Professional Profile */}
          <Route
            path="/professional-profile/:userId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ProfessionalProfileScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          {/* Employer Signup — public, no auth required */}
          <Route path="/employer/register" element={<React.Suspense fallback={null}><EmployerSignupScreen /></React.Suspense>} />
          <Route path="/employer-signup" element={<Navigate to="/employer/register" replace />} />
          <Route path="/employer-login" element={<React.Suspense fallback={null}><EmployerLoginScreen /></React.Suspense>} />
          <Route path="/employer/pricing" element={
            <React.Suspense fallback={null}>
              <AuthenticatedLayout>
                <EmployerPricingScreen />
              </AuthenticatedLayout>
            </React.Suspense>
          } />
          <Route path="/pro" element={<React.Suspense fallback={null}><ProPricingScreen /></React.Suspense>} />
          <Route path="/employer/profile/:businessId" element={<React.Suspense fallback={null}><EmployerProfileScreen /></React.Suspense>} />

          {/* Employer / Company Reviews — open to all authenticated users. */}
          <Route
            path="/admin/reviews"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <AdminReviewsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/referrals"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ReferralsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/social-referrals"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <SocialReferralsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/where-next"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <WhereNextScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/employer-reviews"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <EmployerReviewsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/write-review"
            element={
              <AuthenticatedRoute>
                <WriteReviewScreen />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/write-review/:businessId"
            element={
              <AuthenticatedRoute>
                <WriteReviewScreen />
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/company-reviews/:businessId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <CompanyReviewsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/employer-reviews/:employerId"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <CompanyReviewsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          {/* Verification */}
          <Route
            path="/verification"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <VerificationScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          {/* Settings Routes */}
          <Route
            path="/settings"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <ProfileSettingsScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/settings/background"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <BackgroundScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/settings/lifestyle"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <LifestyleScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/settings/travel"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <TravelExplorationScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/settings/community"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <KnowledgeCommunityScreen />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/account-created"
            element={
              <AuthenticatedRoute>
                <AuthenticatedLayout>
                  <AccountCreatedPage />
                </AuthenticatedLayout>
              </AuthenticatedRoute>
            }
          />

          {/* Wizard Routes - Onboarding Flow */}
          <Route
            path="/background"
            element={
              <AuthenticatedRoute>
                <WizardLayout>
                  <BackgroundScreen />
                </WizardLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/lifestyle"
            element={
              <AuthenticatedRoute>
                <WizardLayout>
                  <LifestyleScreen />
                </WizardLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/travel-exploration"
            element={
              <AuthenticatedRoute>
                <WizardLayout>
                  <TravelExplorationScreen />
                </WizardLayout>
              </AuthenticatedRoute>
            }
          />
          <Route
            path="/knowledge-community"
            element={
              <AuthenticatedRoute>
                <WizardLayout>
                  <KnowledgeCommunityScreen />
                </WizardLayout>
              </AuthenticatedRoute>
            }
          />

          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLoginScreen />} />
          <Route
            path="/admin/dashboard"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminDashboardScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/tipsTripsAndAdvice"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <TipsTripsAdviceListScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/tipsTripsAndAdvice/create"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminPostTipsTripsAdviceScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/tipsTripsAndAdvice/edit/:postId"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminEditTipsTripsAdviceScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/tipsTripsAndAdvice/view/:postId"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <ViewTipsTripsAdviceScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/businesses"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <BusinessListScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/businesses/:id"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <BusinessDetailScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <UsersListScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/employers"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <EmployersListScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminUserViewScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/users/:userId/edit"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminUserEditScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/employers/:employerId"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminEmployerViewScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/employers/:employerId/edit"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminEmployerEditScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <ReportsListScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/reports/:reportId"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminReportEditScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/reports/:reportId/edit"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminReportEditScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/meetups"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminMeetupsScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/marketplace"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminMarketplaceScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/help-requests"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminHelpRequestsScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/service-offers"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminServiceOffersScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/jobs"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminJobsScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/badges"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminBadgesScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />
          <Route
            path="/admin/verifications"
            element={
              <AdminProvider>
                <AdminRoute>
                  <AdminLayout>
                    <AdminVerificationsScreen />
                  </AdminLayout>
                </AdminRoute>
              </AdminProvider>
            }
          />

          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </PageTransition>
      </Suspense>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <PWAInstallPrompt />
    </>
  );
}

export default App;
